import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {
  validateCustomSyntax,
  validateHelm,
  validateIdt,
} from 'notebook/structured-tables/sequence-notations/ValidateNotationsDAO';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  ValidateHelmQuery,
  ValidateIDTQuery,
  CustomServerResolutionResult,
  CustomResolutionQueryType,
  ValidateCustomSyntaxQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

import {getNucleotideTypeForBasesColumnConfig} from '../bases-columns/BasesColumnConstants';
import {BasesColumnDefinition, ColumnDefinition} from '../columns/ColumnDefinitions';

export async function bulkResolveHELMColumnToServerResolutionResult(
  queries: Array<ValidateHelmQuery>
): Promise<Array<CustomServerResolutionResult>> {
  if (!queries.every((query) => query.nucleotideType === queries[0].nucleotideType)) {
    throw Error('Expected all batched queries to have the same nucleotide type');
  }

  const queryResult = await validateHelm(
    queries[0].nucleotideType,
    queries.map((q) => q.helmString)
  );
  return queryResult.serverResolutionResults;
}

export async function bulkResolveIDTColumnToServerResolutionResult(
  queries: Array<ValidateIDTQuery>
): Promise<Array<CustomServerResolutionResult>> {
  if (!queries.every((query) => query.nucleotideType === queries[0].nucleotideType)) {
    throw Error('Expected all batched queries to have the same nucleotide type');
  }

  const queryResult = await validateIdt(
    queries[0].nucleotideType,
    queries.map((q) => q.idtString)
  );
  return queryResult.serverResolutionResults;
}

export async function bulkResolveCustomSyntaxColumnToServerResolutionResult(
  queries: Array<ValidateCustomSyntaxQuery>
): Promise<Array<CustomServerResolutionResult>> {
  if (!queries.every((query) => query.nucleotideType === queries[0].nucleotideType)) {
    throw Error('Expected all batched queries to have the same nucleotide type');
  }
  if (!queries.every((query) => query.customSyntaxName === queries[0].customSyntaxName)) {
    throw Error('Expected all batched queries to have the same custom notation');
  }

  const queryResult = await validateCustomSyntax(
    queries[0].nucleotideType,
    queries[0].customSyntaxName,
    queries.map((q) => q.customSyntaxString)
  );
  return queryResult.serverResolutionResults;
}

export function basesHELMColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const helmString = formatTypedValue(typedValue);
  let nucleotideType: NucleotideType | null = null;
  switch (columnDefinition.type) {
    case TableColumnType.DNA_HELM: {
      nucleotideType = NucleotideType.DNA;
      break;
    }
    case TableColumnType.RNA_HELM: {
      nucleotideType = NucleotideType.RNA;
      break;
    }
    case TableColumnType.BASES: {
      nucleotideType = getNucleotideTypeForBasesColumnConfig(columnDefinition.basesColumnConfig);
      break;
    }
    default:
  }
  assert(nucleotideType != null, 'Could not extract valid nucleotide type from column');

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_HELM,
      nucleotideType,
      helmString,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}

export function basesIDTColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const idtString = formatTypedValue(typedValue);
  let nucleotideType: NucleotideType | null = null;
  switch (columnDefinition.type) {
    case TableColumnType.DNA_IDT: {
      nucleotideType = NucleotideType.DNA;
      break;
    }
    case TableColumnType.RNA_IDT: {
      nucleotideType = NucleotideType.RNA;
      break;
    }
    case TableColumnType.BASES: {
      nucleotideType = getNucleotideTypeForBasesColumnConfig(columnDefinition.basesColumnConfig);
      break;
    }
    default:
  }
  assert(nucleotideType != null, 'Could not extract valid nucleotide type from column');

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_IDT,
      nucleotideType,
      idtString,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}

export function basesCustomSyntaxColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: BasesColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const customSyntaxString = formatTypedValue(typedValue);
  const nucleotideType = getNucleotideTypeForBasesColumnConfig(columnDefinition.basesColumnConfig);
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_VALIDATE_CUSTOM_SYNTAX,
      nucleotideType,
      customSyntaxString,
      customSyntaxName: columnDefinition.basesColumnConfig.customSyntaxName!,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
